import Excel, { Workbook } from "exceljs";
import { UserType } from "src/common/enums";
import {
    ADD_USER_DATA_SHEET_NAME,
    ADD_USER_COLUMN_NAME_EXCEL_ROW,
    MAX_ADD_USER_ROW_COUNT,
} from "src/common/constants";
import { ParsedAliasRow } from "src/models/bulk-add-user-by-alias";
import { getCellText } from "src/common/excel-util";

// The Data-sheet column headers, in order. Not translated: the sheet name and
// header text are kept stable so parsing is language-independent (same rationale
// as the bulk-onboard 'Data' sheet).
export const EXPECTED_HEADERS: string[] = [
    "System Access Credential",
    "User Type",
    "Email",
];

// Carries a translation key as its message so the page can surface it via AlertBar.
export class SheetParseError extends Error {}

// Parses and validates the Data sheet. Throws SheetParseError(translationKey) on
// any structural problem. Returns one ParsedAliasRow per data row, each
// stamped with its original Excel row number.
export const parseSheet = (workbook: Workbook): ParsedAliasRow[] => {
    const worksheet = workbook.getWorksheet(ADD_USER_DATA_SHEET_NAME);
    if (!worksheet) {
        throw new SheetParseError("workbook-is-missing-sheet");
    }

    const headerRow = worksheet.getRow(ADD_USER_COLUMN_NAME_EXCEL_ROW);
    const actualHeaders = EXPECTED_HEADERS.map((_, i) => getCellText(headerRow.getCell(i + 1)).trim());
    const headersMatch = EXPECTED_HEADERS.every((expected, i) => actualHeaders[i] === expected);
    if (!headersMatch) {
        throw new SheetParseError("sheet-missing-or-misordered-columns-add-user");
    }

    const rows: ParsedAliasRow[] = [];
    worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber <= ADD_USER_COLUMN_NAME_EXCEL_ROW) {
            return;
        }
        const alias = getCellText(row.getCell(1)).trim();
        const userType = getCellText(row.getCell(2)).trim().toUpperCase() as UserType;
        const email = getCellText(row.getCell(3)).trim();

        // Skip rows where both mandatory fields are empty — treat as end of data
        if (!alias && !userType) {
            return;
        }

        rows.push({ rowNumber, alias, userType, email });
    });

    if (rows.length === 0) {
        throw new SheetParseError("sheet-missing-rows-add-user");
    }
    if (rows.length > MAX_ADD_USER_ROW_COUNT) {
        throw new SheetParseError("sheet-too-many-rows-add-user-max");
    }

    return rows;
};
